// pages/CompetencyQuiz/TakeTab.js
//
// Start (or resume) an attempt. The server draws the questions and starts the timer when
// Start is pressed; an open attempt is resumed instead of starting a second one.
// Owner 2026-10-26: people taking the quiz do not see their past attempts; IT (IT Admin /
// Results holders) do, here and on the Results tab. The API sends the list to IT only.
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Col, Row, Spinner, Table } from "reactstrap";
import { toast } from "react-toastify";
import { EmptyState, StatTile } from "../../Components/Common/DashboardKit";
import { errorText, getOverview, pick, startAttempt } from "../../services/competencyQuizApi";
import { BandBadge, fmtDateTime, fmtPercent } from "./quizFormat";
import useSingleFlight from "./useSingleFlight";
import useAuth from "../Personal Attendance/useAuth";

export default function TakeTab() {
    const navigate = useNavigate();
    const [overview, setOverview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [starting, setStarting] = useState(false);
    const [error, setError] = useState(null);
    const [notAssigned, setNotAssigned] = useState(false);
    const once = useSingleFlight();
    const { isItAdmin, canAccessModule } = useAuth();
    const showHistory = isItAdmin || canAccessModule("competencyQuiz.results");

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        setNotAssigned(false);
        try {
            setOverview(await getOverview());
        } catch (err) {
            // 403 = the quiz is no longer assigned (finishing it unassigns automatically).
            if (err?.response?.status === 403) setNotAssigned(true);
            else setError(errorText(err, "Could not load the quiz."));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const start = () => once("start", async () => {
        // An attempt already open: go straight to it (works even while IT has the quiz closed).
        const open = pick(overview, "OpenAttemptId", "openAttemptId");
        if (open) { navigate("/competency-quiz/attempt/" + open); return; }
        setStarting(true);
        try {
            const view = await startAttempt();
            const id = pick(view, "AttemptId", "attemptId");
            // A previous attempt ran out of time and was just submitted: show that result.
            if (pick(view, "Status", "status") === "Submitted") navigate(`/competency-quiz/result/${id}`);
            else navigate(`/competency-quiz/attempt/${id}`);
        } catch (err) {
            if (err?.response?.status === 403) setNotAssigned(true);
            else toast.error(errorText(err, "Could not start the quiz."));
            setStarting(false);
            load();
        }
    });

    if (loading) return <div className="text-center py-5"><Spinner size="sm" /> Loading…</div>;
    if (notAssigned) {
        return (
            <EmptyState icon="ri-checkbox-circle-line" tone="success" title="No quiz assigned to you right now"
                message="If you just finished the quiz, it is complete: your result was shown when you submitted, and the quiz has been removed from your assignments. IT will assign it again if a retake is needed." />
        );
    }
    if (error) return <EmptyState icon="ri-error-warning-line" tone="danger" title="Quiz unavailable" message={error} />;

    const minutes = pick(overview, "TimeLimitMinutes", "timeLimitMinutes");
    const openId = pick(overview, "OpenAttemptId", "openAttemptId");
    const isOpen = pick(overview, "IsOpen", "isOpen") !== false;
    const canStart = pick(overview, "CanStart", "canStart") !== false;
    const attempts = pick(overview, "MyAttempts", "myAttempts") || [];
    const submitted = attempts.filter((a) => pick(a, "Status", "status") === "Submitted");
    const best = submitted.reduce((m, a) => Math.max(m, Number(pick(a, "ScorePercent", "scorePercent") || 0)), 0);
    const last = submitted[0];

    return (
        <>
            <Row className="g-3 mb-3">
                <Col md={showHistory ? 8 : 12}>
                    <h5 className="mb-1">{pick(overview, "Title", "title")}</h5>
                    <p className="text-muted mb-2">{pick(overview, "Description", "description")}</p>
                    <ul className="text-muted small mb-3 ps-3">
                        <li>{minutes ? <>You have <b>{minutes} minutes</b>. When the time is up the quiz is submitted as it stands.</> : "There is no time limit."}</li>
                        <li>"Select all that apply" questions count only when every correct option, and nothing else, is chosen.</li>
                        <li>Your answers are saved as you go: you can close the page and come back to the same attempt (the timer keeps running).</li>
                    </ul>
                    {!canStart && !openId && (
                        <div className="alert alert-success py-2 small mb-2">You have completed the quiz and it is no longer assigned to you. IT will assign it again if a retake is needed.</div>
                    )}
                    {canStart && !isOpen && !openId && (
                        <div className="alert alert-warning py-2 small mb-2">The quiz is closed right now.{showHistory ? " Your past results are below." : ""}</div>
                    )}
                    {((canStart && isOpen) || openId) && (
                        <Button color="primary" onClick={start} disabled={starting}>
                            {starting ? <Spinner size="sm" className="me-1" /> : <i className={`${openId ? "ri-play-circle-line" : "ri-edit-box-line"} me-1`} />}
                            {openId ? "Resume my quiz" : "Start the quiz"}
                        </Button>
                    )}
                    {openId && showHistory && <div className="small text-muted mt-2">Past results stay hidden until you submit the quiz you have open.</div>}
                </Col>
                {showHistory && (
                <Col md={4}>
                    <Row className="g-2">
                        <Col xs={6}><StatTile icon="ri-file-list-3-line" label="Attempts" value={submitted.length} tone="primary" subtle /></Col>
                        <Col xs={6}><StatTile icon="ri-trophy-line" label="Best score" value={best} suffix="%" tone="success" subtle /></Col>
                        <Col xs={12}>
                            <div className="small text-muted">
                                Last result: {last ? <>{fmtPercent(pick(last, "ScorePercent", "scorePercent"))} <BandBadge band={pick(last, "Band", "band")} /></> : "none yet"}
                            </div>
                        </Col>
                    </Row>
                </Col>
                )}
            </Row>

            {showHistory && <h6 className="mt-2">My attempts</h6>}
            {!showHistory ? null : attempts.length === 0 ? (
                <EmptyState compact title="No attempts yet" message="Your results will appear here after you submit the quiz." />
            ) : (
                <div className="table-responsive">
                    <Table hover className="align-middle mb-0">
                        <thead className="table-light">
                            <tr>
                                <th>Started</th>
                                <th>Submitted</th>
                                <th className="text-end">Score</th>
                                <th>Band</th>
                                <th className="text-end">Critical misses</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {attempts.map((a) => {
                                const id = pick(a, "AttemptId", "attemptId");
                                const open = pick(a, "Status", "status") !== "Submitted";
                                return (
                                    <tr key={id}>
                                        <td>{fmtDateTime(pick(a, "StartedAt", "startedAt"))}</td>
                                        <td>
                                            {open ? <span className="text-warning">In progress</span> : fmtDateTime(pick(a, "SubmittedAt", "submittedAt"))}
                                            {pick(a, "AutoSubmitted", "autoSubmitted") && <span className="ms-1 small text-muted">(time ran out)</span>}
                                        </td>
                                        <td className="text-end">
                                            {open ? "—" : `${pick(a, "CorrectCount", "correctCount")}/${pick(a, "QuestionCount", "questionCount")} · ${fmtPercent(pick(a, "ScorePercent", "scorePercent"))}`}
                                        </td>
                                        <td><BandBadge band={pick(a, "Band", "band")} /></td>
                                        <td className="text-end">{open ? "—" : pick(a, "CriticalMisses", "criticalMisses")}</td>
                                        <td className="text-end">
                                            {open ? (
                                                <Button size="sm" color="primary" outline onClick={() => navigate(`/competency-quiz/attempt/${id}`)}>Resume</Button>
                                            ) : (
                                                <Button size="sm" color="light" disabled={!!openId} title={openId ? "Hidden while a quiz is in progress" : undefined}
                                                    onClick={() => navigate(`/competency-quiz/result/${id}`)}>View result</Button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                </div>
            )}
        </>
    );
}
